// src/pages/CarDetailPage.jsx - UPDATED with SEO-friendly slug routing
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../styles/CarDetailPage.css";
import { fetchCar } from "../services/car";
import TestDriveModal from "../components/TestDriveModal";
import LoginRequiredModal from "../components/LoginRequiredModal"; // ✅ NEW
import { bookTestDrive } from "../services/testDrive";
import Car360Viewer from "../components/Car360Viewer";
import BookingSummaryModal from "../components/BookingSummaryModal";
import { bookingService } from "../services/bookingService";
import { paymentService } from "../services/paymentService";
import EMICalculator from "../components/finance/EMICalculator";
import SimilarCars from "../components/car/SimilarCars";
import QualityReport from "../components/car/QualityReport";
import QualityReportModal from "../components/car/QualityReportModal";
import BenefitsAddons from "../components/car/BenefitsAddons";
import CarSpecifications from "../components/car/CarSpecifications";
import TopFeatures from "../components/car/TopFeatures";
import FAQ from "../components/car/FAQ";
import LoanEligibilityModal from "../components/finance/LoanEligibilityModal";
import { recordCarView } from "../components/home/RecentlyViewedCars";
import { buildCarSlug } from "../utils/carSlug"; // ✅ NEW

// ✅ FIX: Central image URL builder
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";
const toAbs = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${API_BASE}${path}`;
};

const CarDetailPage = () => {
  // ✅ NEW: supports BOTH route patterns:
  //   /buy-cars/:city/:brand/:model/:variantYear/:id  (SEO slug)
  //   /car/:id                                         (legacy)
  const params = useParams();

  // Extract the numeric ID — it's always the last named param
  const carId = params.id;

  const navigate = useNavigate();
  const [showQualityReport, setShowQualityReport] = useState(false);
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIndex, setImgIndex] = useState(0);
  const [showTestDriveModal, setShowTestDriveModal] = useState(false);
  const [showLoginRequired, setShowLoginRequired] = useState(false); // ✅ NEW
  const [showBookingSummary, setShowBookingSummary] = useState(false);
  const [activeViewMode, setActiveViewMode] = useState("gallery");
  const [active360Tab, setActive360Tab] = useState("exterior");
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingAmount, setBookingAmount] = useState(0);
  const [showLoanEligibility, setShowLoanEligibility] = useState(false);

  const BUSINESS_PHONE = "+919425337223";
  const BUSINESS_WHATSAPP = "919425337223";

  // ✅ NEW: Central gatekeeper for the Test Drive flow.
  // Call this from every "Test Drive" trigger instead of setShowTestDriveModal directly.
  const handleOpenTestDrive = () => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      setShowLoginRequired(true); // show the friendly popup, no scrolling needed
      return;
    }
    setShowTestDriveModal(true);
  };

  // ✅ NEW: User clicked "Login Now" inside the LoginRequiredModal
  const handleLoginRedirect = () => {
    setShowLoginRequired(false);
    // Preserve where the user was, so after login they can come back if you want
    // (optional: pass state to redirect back here after login)
    navigate("/login", { state: { from: window.location.pathname } });
  };

  const handleTestDriveSubmit = async (formData) => {
    try {
      await bookTestDrive(car.id, formData);
      alert("Test drive booked successfully!");
    } catch (error) {
      console.error("Error booking test drive:", error);
      throw error;
    }
  };

  const handleBookNow = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        alert("Please login to book this car");
        navigate("/login");
        return;
      }
      setBookingLoading(true);
      const availabilityCheck = await bookingService.checkAvailability(car.id);
      if (!availabilityCheck.available) {
        alert(availabilityCheck.message || "This car is no longer available for booking");
        setBookingLoading(false);
        return;
      }
      const bookingInfo = await bookingService.calculateBookingAmount(car.id);
      setBookingAmount(bookingInfo.booking_amount_rupees);
      setShowBookingSummary(true);
      setBookingLoading(false);
    } catch (error) {
      console.error("Booking error:", error);
      alert(error.error || "Failed to load booking details. Please try again.");
      setBookingLoading(false);
    }
  };

  const handleConfirmBooking = async () => {
    try {
      setBookingLoading(true);
      const booking = await bookingService.createBooking(car.id);
      const paymentIntent = await paymentService.initiateBookingPayment(booking.id);
      setShowBookingSummary(false);
      loadRazorpayScript(() => {
        initiateRazorpayPayment(paymentIntent, booking);
      });
    } catch (error) {
      console.error("Booking confirmation error:", error);
      alert(error.error || "Failed to initiate payment. Please try again.");
      setBookingLoading(false);
    }
  };

  const loadRazorpayScript = (callback) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = callback;
    script.onerror = () => {
      alert("Failed to load payment gateway. Please try again.");
      setBookingLoading(false);
    };
    document.body.appendChild(script);
  };

  const initiateRazorpayPayment = (paymentIntent, booking) => {
    const options = {
      key: paymentIntent.razorpay_key,
      amount: paymentIntent.amount_paisa,
      currency: "INR",
      name: "Caryugo",
      description: `Booking for ${car.brand} ${car.model}`,
      order_id: paymentIntent.razorpay_order_id,
      handler: async function (response) {
        try {
          const confirmResponse = await paymentService.confirmPayment(
            paymentIntent.payment_id,
            response
          );
          navigate("/payment-success", {
            state: {
              paymentData: confirmResponse,
              paymentIntent,
              booking,
              car,
              type: "booking",
            },
          });
        } catch (error) {
          console.error("Payment confirmation failed:", error);
          alert("Payment verification failed. Please contact support.");
        } finally {
          setBookingLoading(false);
        }
      },
      prefill: { name: "", email: "", contact: "" },
      theme: { color: "#003566" },
      modal: {
        ondismiss: function () {
          alert("Payment cancelled. The booking is still pending.");
          setBookingLoading(false);
        },
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const handleCallNow = () => {
    window.location.href = `tel:${BUSINESS_PHONE}`;
  };

  const handleWhatsApp = () => {
    if (!car) return;
    const message = encodeURIComponent(
      `Hi, I'm interested in the ${car.brand} ${car.model} ${car.variant || ""} (${car.year})\n` +
        `Price: ₹${parseFloat(car.price)?.toLocaleString("en-IN")}\n` +
        `Location: ${car.location}\n\nCould you please provide more details?`
    );
    const whatsappUrl = `https://wa.me/${BUSINESS_WHATSAPP}?text=${message}`;
    window.open(whatsappUrl, "_blank");
  };

  useEffect(() => {
    async function loadCar() {
      try {
        setLoading(true);
        const response = await fetchCar(carId); // ✅ uses extracted numeric ID
        const loadedCar = response.car;

        setCar(loadedCar);
        recordCarView(loadedCar);

        // ✅ Redirect legacy /car/:id URLs to the SEO slug URL
        if (window.location.pathname.startsWith("/car/")) {
          const slugUrl = buildCarSlug(loadedCar);
          navigate(slugUrl, { replace: true });
        }
      } catch (error) {
        console.error("Error loading car:", error);
      } finally {
        setLoading(false);
      }
    }
    loadCar();
  }, [carId]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading car details...</p>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="car-detail-page">
        <div className="not-found-container">
          <h2>Car not found</h2>
          <button onClick={() => navigate(-1)} className="back-button">
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  const exteriorImages = car.exterior_images_urls ?? [];
  const interiorImages = car.interior_images_urls ?? [];
  const galleryImages = car.images_urls ?? [];
  const interiorVideoUrl = car.interior_video_url;

  const get360Images = () => {
    switch (active360Tab) {
      case "exterior":
        return { images: exteriorImages, videoUrl: null };
      case "interior":
        return { images: interiorImages, videoUrl: interiorVideoUrl || null };
      case "gallery":
        return { images: galleryImages, videoUrl: null };
      default:
        return { images: galleryImages, videoUrl: null };
    }
  };

  const { images: viewerImages, videoUrl } = get360Images();

  return (
    <>
      <div className="car-detail-page">
        <button onClick={() => navigate(-1)} className="back-nav-btn">
          <span className="back-arrow">←</span> Back
        </button>

        <div className="car-detail-layout">
          {/* LEFT COLUMN */}
          <div className="gallery-section">
            <div className="view-mode-selector">
              <button
                onClick={() => setActiveViewMode("gallery")}
                className={`view-mode-btn ${activeViewMode === "gallery" ? "active" : ""}`}
              >
                <span>📷</span> Gallery View
              </button>
              <button
                onClick={() => setActiveViewMode("360")}
                className={`view-mode-btn ${activeViewMode === "360" ? "active" : ""}`}
              >
                <span>🔄</span> 360° View
              </button>
            </div>

            {activeViewMode === "gallery" ? (
              <>
                <div className="main-image-container">
                  <img
                    className="main-car-image"
                    src={toAbs(galleryImages[imgIndex])}
                    alt={car.title}
                  />
                </div>
                <div className="thumbnail-gallery">
                  {galleryImages?.map((img, i) => (
                    <div
                      key={i}
                      onClick={() => setImgIndex(i)}
                      className={`thumbnail-item ${imgIndex === i ? "active" : ""}`}
                    >
                      <img src={toAbs(img)} alt={`Thumbnail ${i + 1}`} />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="viewer-360-container">
                  <Car360Viewer
                    images={viewerImages}
                    videoUrl={videoUrl}
                    type={active360Tab}
                  />
                </div>
                <div className="viewer-360-tabs">
                  <button
                    onClick={() => setActive360Tab("exterior")}
                    className={`tab-360-btn ${active360Tab === "exterior" ? "active" : ""}`}
                  >
                    <span className="tab-icon">🚗</span> Exterior ({exteriorImages.length})
                  </button>
                  <button
                    onClick={() => setActive360Tab("interior")}
                    className={`tab-360-btn ${active360Tab === "interior" ? "active" : ""}`}
                  >
                    <span className="tab-icon">{interiorVideoUrl ? "🎥" : "🪑"}</span>{" "}
                    Interior {interiorVideoUrl ? "(Video)" : `(${interiorImages.length})`}
                  </button>
                  <button
                    onClick={() => setActive360Tab("gallery")}
                    className={`tab-360-btn ${active360Tab === "gallery" ? "active" : ""}`}
                  >
                    <span className="tab-icon">📷</span> Gallery ({galleryImages.length})
                  </button>
                </div>
              </>
            )}

            {/* Car Overview */}
            <div className="specifications-card">
              <h3 className="car-ove0rview-title">Car Overview</h3>
              <div className="specs-grid-layout">
                <div className="spec-box">
                  <span className="spec-label">Make Year</span>
                  <span className="spec-value">{car.make_year || car.year}</span>
                </div>
                <div className="spec-box">
                  <span className="spec-label">Registration Year</span>
                  <span className="spec-value">{car.registration_year || car.year}</span>
                </div>
                <div className="spec-box">
                  <span className="spec-label">Fuel Type</span>
                  <span className="spec-value">{car.fuel_type || "N/A"}</span>
                </div>
                <div className="spec-box">
                  <span className="spec-label">Body Type</span>
                  <span className="spec-value">{car.body_type || "N/A"}</span>
                </div>
                <div className="spec-box">
                  <span className="spec-label">Km driven</span>
                  <span className="spec-value">{car.km_driven || car.mileage}</span>
                </div>
                <div className="spec-box">
                  <span className="spec-label">Transmission</span>
                  <span className="spec-value">{car.transmission}</span>
                </div>
                <div className="spec-box">
                  <span className="spec-label">No. of Owner</span>
                  <span className="spec-value">{car.owner_count} Owner</span>
                </div>
                <div className="spec-box">
                  <span className="spec-label">Insurance Type</span>
                  <span className="spec-value">{car.insurance_type || "N/A"}</span>
                </div>
                <div className="spec-box">
                  <span className="spec-label">Insurance Validity</span>
                  <span className="spec-value">{car.insurance_validity || "N/A"}</span>
                </div>
                <div className="spec-box">
                  <span className="spec-label">RTO</span>
                  <span className="spec-value">{car.rto || "N/A"}</span>
                </div>
                <div className="spec-box">
                  <span className="spec-label">Car Location</span>
                  <span className="spec-value">{car.location}</span>
                </div>
              </div>
            </div>

            <QualityReport car={car} onViewReport={() => setShowQualityReport(true)} />
            <BenefitsAddons car={car} />
            <CarSpecifications
              car={car}
              onBookNow={handleBookNow}
              onTestDrive={handleOpenTestDrive} // ✅ CHANGED: goes through the auth gate now
            />
            <TopFeatures
              car={car}
              onBookNow={handleBookNow}
              onTestDrive={handleOpenTestDrive} // ✅ CHANGED
            />

            <div className="emi-section-wrapper">
              <EMICalculator carPrice={car.price} />
            </div>
            <button
              className="secondary-cta"
              onClick={() => setShowLoanEligibility(true)}
            >
              💰 Check Loan Eligibility
            </button>

            <SimilarCars currentCar={car} />
            <FAQ />

            <div className="browse-more-section">
              <button
                onClick={() => navigate("/buy-cars")}
                className="browse-more-btn"
              >
                Browse More Cars
              </button>
            </div>
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="info-sidebar">
            <div className="car-info-card">
              <h1 className="car-title">{car.title}</h1>
              <h2 className="car-title">{car.variant}</h2>

              {car.availability && (
                <div className={`availability-badge ${car.availability}`}>
                  {car.availability === "in_stock" && "✓ In Stock"}
                  {car.availability === "booked" && "🔒 Booked"}
                  {car.availability === "upcoming" && "🔜 Coming Soon"}
                </div>
              )}

              <div className="quick-info-tags">
                <span className="info-tag">{car.fuel_type || "Fuel"}</span>
                <span className="info-tag">{car.transmission || "Transmission"}</span>
                <span className="info-tag">{car.year}</span>
                <span className="info-tag">{car.km_driven} km</span>
              </div>

              <div className="pricing-section">
                <div className="price-container">
                  <span className="price-label">Fixed on road price</span>
                  <span className="price-amount">
                    ₹{parseFloat(car.price)?.toLocaleString("en-IN")}
                  </span>
                </div>
                <div className="emi-details">
                  <span className="emi-label">
                    or ₹{Math.round(parseFloat(car.price) / 60).toLocaleString("en-IN")}/m
                  </span>
                  <span className="emi-amount">Starting EMI</span>
                </div>
              </div>

              <div className="action-buttons-grid">
                <button
                  className="primary-cta book-now-btn"
                  onClick={handleBookNow}
                  disabled={bookingLoading}
                >
                  {bookingLoading ? (
                    <>
                      <span className="spinner-small"></span> Loading...
                    </>
                  ) : (
                    <> Book Now</>
                  )}
                </button>

                <button
                  className="secondary-cta"
                  onClick={handleOpenTestDrive} // ✅ CHANGED: was setShowTestDriveModal(true)
                >
                  FREE TEST DRIVE
                </button>

                <button className="contact-btn call-btn" onClick={handleCallNow}>
                  📞 Call Now
                </button>

                <button
                  className="secondary-cta"
                  onClick={() => setShowLoanEligibility(true)}
                >
                  💰 Check Loan Eligibility
                </button>

                <button className="contact-btn whatsapp-btn" onClick={handleWhatsApp}>
                  💬 WhatsApp
                </button>
              </div>
            </div>

            <div className="benefits-card">
              <h3 className="benefits-title">
                <span className="shield-icon">🛡️</span>
                Why Buy From Us
              </h3>
              <div className="benefits-list">
                <div className="benefit-item">
                  <span className="benefit-icon">✓</span>
                  <div className="benefit-text">
                    <strong>Quality Assured</strong>
                    <span>Certified pre-owned vehicles</span>
                  </div>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">✓</span>
                  <div className="benefit-text">
                    <strong>Easy Financing</strong>
                    <span>Flexible payment options</span>
                  </div>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">✓</span>
                  <div className="benefit-text">
                    <strong>7-Day Return</strong>
                    <span>No questions asked</span>
                  </div>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">✓</span>
                  <div className="benefit-text">
                    <strong>Free RC Transfer</strong>
                    <span>Hassle-free documentation</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Test Drive modal only ever opens for logged-in users now */}
      <TestDriveModal
        isOpen={showTestDriveModal}
        onClose={() => setShowTestDriveModal(false)}
        car={car}
        onSubmit={handleTestDriveSubmit}
      />

      {/* ✅ NEW: Friendly "please login" popup instead of a buried error message */}
      <LoginRequiredModal
        isOpen={showLoginRequired}
        onClose={() => setShowLoginRequired(false)}
        onLogin={handleLoginRedirect}
        message="Please login first to book a test drive."
      />

      <BookingSummaryModal
        isOpen={showBookingSummary}
        onClose={() => {
          setShowBookingSummary(false);
          setBookingLoading(false);
        }}
        car={car}
        bookingAmount={bookingAmount}
        onConfirmBooking={handleConfirmBooking}
        loading={bookingLoading}
      />

      <QualityReportModal
        isOpen={showQualityReport}
        onClose={() => setShowQualityReport(false)}
        car={car}
        onBookNow={handleBookNow}
        onTestDrive={handleOpenTestDrive} // ✅ CHANGED
      />

      <LoanEligibilityModal
        isOpen={showLoanEligibility}
        onClose={() => setShowLoanEligibility(false)}
        car={car}
      />
    </>
  );
};

export default CarDetailPage;